
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Job {
  id: string;
  title?: string;
  company?: string;
  location?: string;
  description?: string;
  salary_min?: number;
  salary_max?: number;
  experience_required?: number;
  skills?: string[];
  status?: string;
  type?: string;
  user_id: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  deleted_by?: string;
  document_url?: string;
  posted?: string;
  requirements?: string;
  benefits?: string;
  application_deadline?: string;
  employment_type?: string;
  remote_allowed?: boolean;
  salary_currency?: string;
  client_id?: string;
  posted_date?: string;
  applicants?: number;
}

export const useJobs = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const jobsQuery = useQuery({
    queryKey: ['jobs', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) {
        console.log('No authenticated user, returning empty jobs array');
        return [];
      }

      console.log('Fetching jobs for user:', session.user.id);

      // Query with explicit user_id filter for strict user isolation
      // RLS policies will also enforce this at database level
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('user_id', session.user.id) // Explicit user filtering
        .is('deleted_at', null) // Only get non-deleted jobs
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching jobs:', error);
        throw error;
      }

      console.log('Successfully fetched jobs:', data?.length || 0);
      return data as Job[];
    },
    enabled: !!session?.user?.id,
  });

  const createJobMutation = useMutation({
    mutationFn: async (jobData: Partial<Job>) => {
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('jobs')
        .insert([{ 
          ...jobData, 
          user_id: session.user.id, // Ensure job belongs to current user
          title: jobData.title || ''
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Job created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create job');
      console.error('Error creating job:', error);
    },
  });

  const updateJobMutation = useMutation({
    mutationFn: async ({ id, ...jobData }: Partial<Job> & { id: string }) => {
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('jobs')
        .update({ ...jobData, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', session.user.id) // Ensure user can only update their own jobs
        .is('deleted_at', null)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Job updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update job');
      console.error('Error updating job:', error);
    },
  });

  return {
    ...jobsQuery,
    createJob: createJobMutation.mutate,
    updateJob: updateJobMutation.mutate,
    isCreating: createJobMutation.isPending,
    isUpdating: updateJobMutation.isPending,
  };
};
